import { useEffect, useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutData } from '@/mocks/about';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9rfg7snlsngrm4lmbe0';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 开云官网';
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于开云',
            description: '开云平台品牌介绍、信誉资质与联系方式',
            isPartOf: {
              '@type': 'WebSite',
              name: '开云官网',
              url: 'https://www.kaiyun.com',
            },
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            {/* Brand Intro */}
            <section className="mb-16 md:mb-20">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <p className="text-xs font-semibold text-primary-500 tracking-widest uppercase mb-2 font-label">品牌介绍</p>
                  <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-4">
                    {aboutData.brandIntro.title}
                  </h1>
                  <p className="text-lg font-heading text-foreground-700 mb-4 font-semibold">
                    {aboutData.brandIntro.subtitle}
                  </p>
                  <p className="text-sm text-foreground-500 leading-relaxed mb-6">
                    {aboutData.brandIntro.description}
                  </p>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 rounded-lg bg-primary-50">
                      <div className="text-xl font-bold font-heading text-primary-600">10年+</div>
                      <div className="text-[11px] text-foreground-500">品牌历程</div>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-accent-50">
                      <div className="text-xl font-bold font-heading text-accent-600">500万+</div>
                      <div className="text-[11px] text-foreground-500">活跃用户</div>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-secondary-50">
                      <div className="text-xl font-bold font-heading text-secondary-600">300+</div>
                      <div className="text-[11px] text-foreground-500">覆盖城市</div>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden">
                  <img
                    src={aboutData.brandIntro.image}
                    alt="开云品牌"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </section>

            {/* Mission & Values */}
            <section className="mb-16 md:mb-20 py-12 md:py-16 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
              <div className="max-w-[1280px] mx-auto">
                <div className="text-center mb-10">
                  <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6">
                    {aboutData.mission.vision}
                  </h2>
                  <p className="text-sm text-foreground-500 max-w-2xl mx-auto">
                    {aboutData.mission.mission}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {aboutData.mission.values.map((val) => (
                    <div key={val.title} className="bg-background-50 rounded-lg p-5 border border-background-200/50 text-center">
                      <h3 className="text-base font-semibold font-heading text-foreground-900 mb-2">{val.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{val.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Milestones */}
            <section className="mb-16 md:mb-20">
              <div className="text-center mb-10">
                <p className="text-xs font-semibold text-accent-500 tracking-widest uppercase mb-2 font-label">发展历程</p>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">品牌里程碑</h2>
              </div>
              <div className="relative max-w-3xl mx-auto">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 -translate-x-1/2 hidden md:block"></div>
                <div className="space-y-8">
                  {aboutData.milestones.map((mile, index) => (
                    <div key={mile.year} className={`relative flex flex-col md:flex-row gap-4 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="flex-1 md:text-right hidden md:block">
                        {index % 2 === 0 && (
                          <div className="p-4 rounded-lg bg-background-100 border border-background-200/50">
                            <h3 className="text-sm font-semibold font-heading text-foreground-900 mb-1">{mile.title}</h3>
                            <p className="text-xs text-foreground-500">{mile.description}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-4 md:justify-center">
                        <div className="w-8 h-8 rounded-full bg-accent-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 z-10 font-label">
                          {mile.year.slice(2)}
                        </div>
                        <div className="md:hidden flex-1 p-4 rounded-lg bg-background-100 border border-background-200/50">
                          <h3 className="text-sm font-semibold font-heading text-foreground-900 mb-1">{mile.title}</h3>
                          <p className="text-xs text-foreground-500">{mile.description}</p>
                        </div>
                      </div>
                      <div className="flex-1 hidden md:block">
                        {index % 2 !== 0 && (
                          <div className="p-4 rounded-lg bg-background-100 border border-background-200/50">
                            <h3 className="text-sm font-semibold font-heading text-foreground-900 mb-1">{mile.title}</h3>
                            <p className="text-xs text-foreground-500">{mile.description}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Reputation */}
            <section id="reputation" className="mb-16 md:mb-20">
              <div className="text-center mb-10">
                <p className="text-xs font-semibold text-secondary-500 tracking-widest uppercase mb-2 font-label">品牌信誉</p>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                  {aboutData.reputation.title}
                </h2>
                <p className="text-sm text-foreground-500">{aboutData.reputation.subtitle}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
                {aboutData.reputation.certifications.map((cert) => (
                  <div key={cert.name} className="bg-background-50 border border-background-200/60 rounded-lg p-5 text-center">
                    <div className="w-11 h-11 rounded-lg bg-secondary-100 flex items-center justify-center mx-auto mb-3">
                      <i className={`${cert.icon} text-secondary-600 text-xl`}></i>
                    </div>
                    <h3 className="text-sm font-semibold font-heading text-foreground-900 mb-1.5">{cert.name}</h3>
                    <p className="text-[11px] text-foreground-500">{cert.description}</p>
                  </div>
                ))}
              </div>

              <div className="max-w-2xl mx-auto">
                <h3 className="text-base font-semibold font-heading text-foreground-900 mb-4 text-center">荣誉奖项</h3>
                <div className="bg-background-50 border border-background-200/60 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-background-200/60 bg-background-100">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-600 font-label">年份</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-600 font-label">奖项名称</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-600 font-label">颁发机构</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aboutData.reputation.awards.map((award, i) => (
                        <tr key={i} className="border-b border-background-200/40 last:border-0">
                          <td className="px-5 py-3 text-xs text-foreground-700">{award.year}</td>
                          <td className="px-5 py-3 text-xs text-foreground-800 font-medium">{award.name}</td>
                          <td className="px-5 py-3 text-xs text-foreground-500">{award.org}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* Contact */}
            <section id="contact">
              <div className="text-center mb-10">
                <p className="text-xs font-semibold text-primary-500 tracking-widest uppercase mb-2 font-label">联系我们</p>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                  {aboutData.contact.title}
                </h2>
                <p className="text-sm text-foreground-500">{aboutData.contact.subtitle}</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div className="space-y-5">
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200/50">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                      <p className="text-xs text-foreground-500">{aboutData.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200/50">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服热线</h3>
                      <p className="text-xs text-foreground-500">{aboutData.contact.phone}</p>
                      <p className="text-[11px] text-foreground-400 mt-1">{aboutData.contact.workingHours}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200/50">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                      <p className="text-xs text-foreground-500">{aboutData.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex gap-3 pt-2">
                    {aboutData.contact.socialLinks.map((social) => (
                      <a
                        key={social.name}
                        href={`#${social.name}`}
                        className="w-9 h-9 rounded-lg bg-background-100 border border-background-200/50 flex items-center justify-center text-foreground-400 hover:bg-primary-500 hover:text-white hover:border-primary-500 transition-all duration-200"
                      >
                        <i className={`${social.icon}`}></i>
                      </a>
                    ))}
                  </div>
                </div>

                <div className="bg-background-100 border border-background-200/50 rounded-lg p-6" data-readdy-form>
                  <h3 className="text-base font-semibold font-heading text-foreground-900 mb-5">发送消息</h3>
                  <form data-readdy-form className="space-y-4" onSubmit={handleSubmit}>
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        电话
                      </label>
                      <input
                        type="tel"
                        id="contact-phone"
                        name="phone"
                        className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的电话"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        留言内容 <span className="text-accent-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full px-5 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap"
                    >
                      {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-xs text-secondary-600 mt-3 text-center">消息已成功发送，我们会尽快回复您！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-xs text-accent-600 mt-3 text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}